import { Suspense, useContext, useEffect } from 'react';

import { Spinner } from '@platform/elements';
import type { Extension } from '@interhyp/platform-plugin';
import type { Presets } from '@interhyp/platform-tealium';
import { TealiumContext, TealiumProvider } from '@interhyp/platform-tealium';

import type { ModuleName } from '../../types/ModuleName.js';

import { ErrorBoundary } from './ErrorBoundary.js';
import { ModuleProvider } from './ModuleProvider.js';
import { WebsiteExtension } from './WebsiteExtension.js';

export interface AppShellProps {
  name: ModuleName;
  props: unknown;
  provide?: Extension[];
  tealium: {
    presets: Presets;
  };
}

const TealiumLoaded = () => {
  const { setTealiumLoaded } = useContext(TealiumContext);

  useEffect(() => {
    const loaded = (window as any).utag !== undefined;

    if (loaded) {
      setTealiumLoaded();
    }

    const listener = () => setTealiumLoaded();

    window.addEventListener('tealium::loaded', listener);
    return () => {
      window.removeEventListener('tealium::loaded', listener);
    };
  }, [setTealiumLoaded]);

  return null;
};

export const AppShell = ({ name, props, provide, tealium: { presets } }: AppShellProps) => {
  return (
    <div>
      <TealiumProvider presets={presets}>
        <ErrorBoundary>
          <TealiumLoaded />
          <Suspense
            fallback={
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <Spinner size={40} />
              </div>
            }
          >
            <ModuleProvider name={name} provide={provide}>
              <WebsiteExtension props={props} />
            </ModuleProvider>
          </Suspense>
        </ErrorBoundary>
      </TealiumProvider>
    </div>
  );
};
